import { useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Settings, 
  Palette, 
  Layout, 
  Type, 
  Box,
  Eye,
  Monitor,
  Smartphone,
  Tablet,
  Plus,
  Trash2,
  MousePointer
} from "lucide-react";
import { PageElement } from "../VisualPageBuilder";

interface PropertiesPanelProps {
  selectedElement: PageElement | null;
  globalStyles: any;
  onUpdateElement: (elementId: string, updates: Partial<PageElement>) => void;
  onUpdateGlobalStyles: (styles: any) => void;
}

export default function PropertiesPanel({
  selectedElement,
  globalStyles,
  onUpdateElement,
  onUpdateGlobalStyles
}: PropertiesPanelProps) {
  const [activeDevice, setActiveDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  const updateElementContent = useCallback((field: string, value: any) => {
    if (!selectedElement) return;
    onUpdateElement(selectedElement.id, {
      content: {
        ...selectedElement.content,
        [field]: value
      }
    });
  }, [selectedElement, onUpdateElement]);

  const updateElementStyles = useCallback((field: string, value: any) => {
    if (!selectedElement) return;
    onUpdateElement(selectedElement.id, {
      styles: {
        ...selectedElement.styles,
        [field]: value
      }
    });
  }, [selectedElement, onUpdateElement]);

  const updateElementSettings = useCallback((field: string, value: any) => {
    if (!selectedElement) return;
    onUpdateElement(selectedElement.id, {
      settings: {
        ...selectedElement.settings,
        [field]: value
      }
    });
  }, [selectedElement, onUpdateElement]);

  const updateElementName = useCallback((name: string) => {
    if (!selectedElement) return;
    onUpdateElement(selectedElement.id, { name });
  }, [selectedElement, onUpdateElement]);

  // Render content-specific properties
  const renderContentProperties = () => {
    if (!selectedElement) return null;

    switch (selectedElement.type) {
      case 'text':
      case 'heading':
        return (
          <div className="space-y-4">
            <div>
              <Label>Text</Label>
              <Textarea
                value={selectedElement.content.text || ''}
                onChange={(e) => updateElementContent('text', e.target.value)}
                placeholder="Text eingeben..."
                rows={3}
              />
            </div>
            {selectedElement.type === 'heading' && (
              <div>
                <Label>Tag</Label>
                <Select
                  value={selectedElement.content.tag || 'h2'}
                  onValueChange={(value) => updateElementContent('tag', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="h1">H1</SelectItem>
                    <SelectItem value="h2">H2</SelectItem>
                    <SelectItem value="h3">H3</SelectItem>
                    <SelectItem value="h4">H4</SelectItem>
                    <SelectItem value="h5">H5</SelectItem>
                    <SelectItem value="h6">H6</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        );

      case 'button':
        return (
          <div className="space-y-4">
            <div>
              <Label>Button Text</Label>
              <Input
                value={selectedElement.content.text || ''}
                onChange={(e) => updateElementContent('text', e.target.value)}
                placeholder="Button Text"
              />
            </div>
            <div>
              <Label>Link</Label>
              <Input
                value={selectedElement.content.link || ''}
                onChange={(e) => updateElementContent('link', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Variante</Label>
              <Select
                value={selectedElement.content.variant || 'default'}
                onValueChange={(value) => updateElementContent('variant', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Standard</SelectItem>
                  <SelectItem value="destructive">Destructive</SelectItem>
                  <SelectItem value="outline">Outline</SelectItem>
                  <SelectItem value="secondary">Secondary</SelectItem>
                  <SelectItem value="ghost">Ghost</SelectItem>
                  <SelectItem value="link">Link</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            <div>
              <Label>Bild URL</Label>
              <Input
                value={selectedElement.content.src || ''}
                onChange={(e) => updateElementContent('src', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Alt Text</Label>
              <Input
                value={selectedElement.content.alt || ''}
                onChange={(e) => updateElementContent('alt', e.target.value)}
                placeholder="Bildbeschreibung"
              />
            </div>
            <div>
              <Label>Bildunterschrift</Label>
              <Input
                value={selectedElement.content.caption || ''}
                onChange={(e) => updateElementContent('caption', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
        );

      case 'hero':
        return (
          <div className="space-y-4">
            <div>
              <Label>Titel</Label>
              <Input
                value={selectedElement.content.title || ''}
                onChange={(e) => updateElementContent('title', e.target.value)}
                placeholder="Hero Titel"
              />
            </div>
            <div>
              <Label>Untertitel</Label>
              <Textarea
                value={selectedElement.content.subtitle || ''}
                onChange={(e) => updateElementContent('subtitle', e.target.value)}
                placeholder="Hero Untertitel"
                rows={2}
              />
            </div>
            <div>
              <Label>CTA Text</Label>
              <Input
                value={selectedElement.content.ctaText || ''}
                onChange={(e) => updateElementContent('ctaText', e.target.value)}
                placeholder="Call-to-Action"
              />
            </div>
            <div>
              <Label>CTA Link</Label>
              <Input
                value={selectedElement.content.ctaLink || ''}
                onChange={(e) => updateElementContent('ctaLink', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Hintergrundbild</Label>
              <Input
                value={selectedElement.content.backgroundImage || ''}
                onChange={(e) => updateElementContent('backgroundImage', e.target.value)}
                placeholder="https://... (optional)"
              />
            </div>
          </div>
        );

      case 'spacer':
        return (
          <div className="space-y-4">
            <div>
              <Label>Höhe</Label>
              <Input
                value={selectedElement.content.height || '32px'}
                onChange={(e) => updateElementContent('height', e.target.value)}
                placeholder="32px"
              />
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4">
            <div>
              <Label>Titel</Label>
              <Input
                value={selectedElement.content.title || ''}
                onChange={(e) => updateElementContent('title', e.target.value)}
                placeholder="Formular Titel"
              />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={selectedElement.content.description || ''}
                onChange={(e) => updateElementContent('description', e.target.value)}
                placeholder="Formular Beschreibung"
                rows={2}
              />
            </div>
            <div>
              <Label>Submit Button Text</Label>
              <Input
                value={selectedElement.content.submitText || ''}
                onChange={(e) => updateElementContent('submitText', e.target.value)}
                placeholder="Nachricht senden"
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-muted-foreground">
            <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Keine Inhaltsoptionen verfügbar</p>
          </div>
        );
    }
  };

  // Render style properties
  const renderStyleProperties = () => {
    if (!selectedElement) return null;

    return (
      <div className="space-y-6">
        {/* Layout */}
        <div className="space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <Layout className="w-4 h-4" />
            Layout
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Breite</Label>
              <Input
                value={selectedElement.styles.width || ''}
                onChange={(e) => updateElementStyles('width', e.target.value)}
                placeholder="auto"
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Höhe</Label>
              <Input
                value={selectedElement.styles.height || ''}
                onChange={(e) => updateElementStyles('height', e.target.value)}
                placeholder="auto"
                className="h-8"
              />
            </div>
          </div>
          
          {/* Position controls for free layout */}
          {selectedElement.styles.position === 'absolute' && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-2">
                <MousePointer className="w-4 h-4" />
                <Label className="text-sm font-medium">Position (Freier Modus)</Label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">X (Links)</Label>
                  <Input
                    value={selectedElement.styles.left?.replace('px', '') || '0'}
                    onChange={(e) => updateElementStyles('left', `${e.target.value}px`)}
                    placeholder="0"
                    className="h-8"
                    type="number"
                  />
                </div>
                <div>
                  <Label className="text-xs">Y (Oben)</Label>
                  <Input
                    value={selectedElement.styles.top?.replace('px', '') || '0'}
                    onChange={(e) => updateElementStyles('top', `${e.target.value}px`)}
                    placeholder="0"
                    className="h-8"
                    type="number"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Spacing */}
        <div className="space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <Box className="w-4 h-4" />
            Abstand
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Padding</Label>
              <Input
                value={selectedElement.styles.padding || ''}
                onChange={(e) => updateElementStyles('padding', e.target.value)}
                placeholder="16px"
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Margin</Label>
              <Input
                value={selectedElement.styles.margin || ''}
                onChange={(e) => updateElementStyles('margin', e.target.value)}
                placeholder="0px"
                className="h-8"
              />
            </div>
          </div>
        </div>

        {/* Typography */}
        <div className="space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <Type className="w-4 h-4" />
            Typografie
          </h4>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Schriftgröße</Label>
              <Input
                value={selectedElement.styles.fontSize || ''}
                onChange={(e) => updateElementStyles('fontSize', e.target.value)}
                placeholder="16px"
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Schriftgewicht</Label>
              <Select
                value={selectedElement.styles.fontWeight || ''}
                onValueChange={(value) => updateElementStyles('fontWeight', value)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Normal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="300">Light</SelectItem>
                  <SelectItem value="400">Normal</SelectItem>
                  <SelectItem value="500">Medium</SelectItem>
                  <SelectItem value="600">Semibold</SelectItem>
                  <SelectItem value="700">Bold</SelectItem>
                  <SelectItem value="800">Extra Bold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Textausrichtung</Label>
              <Select
                value={selectedElement.styles.textAlign || ''}
                onValueChange={(value) => updateElementStyles('textAlign', value)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Links" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Links</SelectItem>
                  <SelectItem value="center">Zentriert</SelectItem>
                  <SelectItem value="right">Rechts</SelectItem>
                  <SelectItem value="justify">Blocksatz</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Colors */}
        <div className="space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Farben
          </h4>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Textfarbe</Label>
              <Input
                type="color"
                value={selectedElement.styles.color || '#000000'}
                onChange={(e) => updateElementStyles('color', e.target.value)}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Hintergrundfarbe</Label>
              <Input
                type="color"
                value={selectedElement.styles.backgroundColor || '#ffffff'}
                onChange={(e) => updateElementStyles('backgroundColor', e.target.value)}
                className="h-8"
              />
            </div>
          </div>
        </div>

        {/* Border */}
        <div className="space-y-3">
          <h4 className="font-medium">Rahmen</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Rahmen</Label>
              <Input
                value={selectedElement.styles.border || ''}
                onChange={(e) => updateElementStyles('border', e.target.value)}
                placeholder="1px solid #ccc"
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Rundung</Label>
              <Input
                value={selectedElement.styles.borderRadius || ''}
                onChange={(e) => updateElementStyles('borderRadius', e.target.value)}
                placeholder="0px"
                className="h-8"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!selectedElement) {
    return (
      <div className="p-4 space-y-6">
        {/* Global Styles */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Globale Stile
            </CardTitle>
            <CardDescription className="text-xs">
              Einstellungen für die gesamte Seite
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">Primärfarbe</Label>
              <Input
                type="color"
                value={globalStyles?.colors?.primary || '#3b82f6'}
                onChange={(e) => onUpdateGlobalStyles({
                  ...globalStyles,
                  colors: { ...globalStyles?.colors, primary: e.target.value }
                })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Sekundärfarbe</Label>
              <Input
                type="color"
                value={globalStyles?.colors?.secondary || '#6b7280'}
                onChange={(e) => onUpdateGlobalStyles({
                  ...globalStyles,
                  colors: { ...globalStyles?.colors, secondary: e.target.value }
                })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Hintergrundfarbe</Label>
              <Input
                type="color"
                value={globalStyles?.colors?.background || '#ffffff'}
                onChange={(e) => onUpdateGlobalStyles({
                  ...globalStyles,
                  colors: { ...globalStyles?.colors, background: e.target.value }
                })}
                className="h-8"
              />
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardContent className="p-4 text-center">
            <Eye className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm text-muted-foreground">
              Wählen Sie ein Element aus, um seine Eigenschaften zu bearbeiten.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Element Info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-xs">
              {selectedElement.type}
            </Badge>
            <Switch
              checked={selectedElement.settings.isVisible ?? true}
              onCheckedChange={(checked) => updateElementSettings('isVisible', checked)}
            />
          </div>
          <div>
            <Label className="text-xs">Element Name</Label>
            <Input
              value={selectedElement.name}
              onChange={(e) => updateElementName(e.target.value)}
              className="h-8 mt-1"
            />
          </div>
        </CardHeader>
      </Card>

      {/* Properties Tabs */}
      <Tabs defaultValue="content" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="content">Inhalt</TabsTrigger>
          <TabsTrigger value="style">Style</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Inhalt bearbeiten</CardTitle>
            </CardHeader>
            <CardContent>
              {renderContentProperties()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="style" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Style anpassen</CardTitle>
            </CardHeader>
            <CardContent>
              {renderStyleProperties()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}